import { constToName } from 'helpers/textHelpers';
import { OperationType } from 'components/RecordsListView/constants';
import { formatEnumValue } from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/helpers';
import {
  applicationMethodOptions,
  timingEventOptions
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/selectOptions';
import {
  ApplicationMethod,
  TimingEvent
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { TableItem } from '../../components/Table';
import { fieldBodyGenerator } from '../tableHelpers';
import {
  displayText,
  displayDate,
  formatValueForWorkOrder
} from '../formatHelpers';
import { OperationPDFData } from '../..';
import { Locale } from '../../locales';

type RawTableRow = [string | ReturnType<typeof fieldBodyGenerator>, string];

const getLabels = (isWorkOrder: boolean, locale: Locale) => ({
  reiExpiration: isWorkOrder
    ? locale.details.rei_estimation
    : locale.details.rei_expiration,
  phiExpiration: isWorkOrder
    ? locale.details.phi_estimation
    : locale.details.phi_expiration,
  startDate: isWorkOrder
    ? locale.details.target_time
    : locale.details.start_time,
  endDate: isWorkOrder ? locale.details.actual_time : locale.details.end_time
});

const createRestrictionLabel = (label: string, fieldLabel: string) =>
  fieldBodyGenerator(fieldLabel, { width: '42%', fontSize: 9 }, [
    fieldBodyGenerator(label, { fontSize: 11 })
  ]);

const formatRowsToTableItems = (rows: RawTableRow[]) =>
  rows.map(([label, value]) => [
    typeof label === 'string'
      ? fieldBodyGenerator(label, { width: '42%', fontSize: 11 })
      : label,
    fieldBodyGenerator(value, {
      marginTop: 1,
      paddingLeft: 8,
      width: '58%',
      fontWeight: 'normal'
    })
  ]);

// eslint-disable-next-line import/prefer-default-export
export const mapDetailsToTable = (
  details: OperationPDFData,
  locale: Locale,
  operationType: OperationType = OperationType.APPLICATION
): TableItem[][] => {
  const isWorkOrder = operationType === OperationType.WORK_ORDER;
  const labels = getLabels(isWorkOrder, locale);

  const authorizationDisplay: RawTableRow = [
    locale.details.authorization,
    locale.common.getByDisplayText(
      details?.authorization?.authorizedBy ?? '',
      displayDate(details?.authorization?.authorizationDate ?? '')
    )
  ];

  const formattedStartDate = isWorkOrder
    ? `${locale.details.start_time_short}: ${displayDate(
        details.startDate,
        'datetime'
      )}`
    : displayDate(details.startDate, 'datetime');

  const startDate: RawTableRow = [labels.startDate, formattedStartDate];

  const endDate: RawTableRow = [
    labels.endDate,
    isWorkOrder
      ? formatValueForWorkOrder(displayDate(details.endDate, 'datetime'))
      : displayDate(details.endDate, 'datetime')
  ];

  const workType: RawTableRow = [
    locale.details.work_type,
    displayText(constToName(details.type))
  ];

  const formattedTimingEvent = formatEnumValue(
    details.timingEvent as TimingEvent,
    TimingEvent,
    timingEventOptions
  );

  const timingEvent: RawTableRow = [
    locale.details.timing_event,
    isWorkOrder
      ? formatValueForWorkOrder(displayText(formattedTimingEvent))
      : displayText(formattedTimingEvent)
  ];

  const formattedApplicationMethod = formatEnumValue(
    details.applicationMethod as ApplicationMethod,
    ApplicationMethod,
    applicationMethodOptions
  );

  const applicationMethod: RawTableRow = [
    locale.details.application_method,
    isWorkOrder
      ? formatValueForWorkOrder(displayText(formattedApplicationMethod))
      : displayText(formattedApplicationMethod)
  ];

  const reiExpiration: RawTableRow = [
    createRestrictionLabel(
      labels.reiExpiration,
      locale.details.most_restrictive
    ),
    displayDate(details.reiExpirationDate, 'datetime')
  ];

  const phiExpiration: RawTableRow = [
    createRestrictionLabel(
      labels.phiExpiration,
      locale.details.most_restrictive
    ),
    displayDate(details.phiExpirationDate, 'datetime')
  ];

  const equipment: RawTableRow = [
    locale.details.equipment,
    isWorkOrder
      ? formatValueForWorkOrder(displayText(details.equipmentIds))
      : displayText(details.equipmentIds)
  ];

  const recordCreated: RawTableRow = [
    locale.details.record_created,
    locale.common.getByDisplayText(
      details.createdBy ?? '',
      displayDate(details.createdAt, 'datetime')
    )
  ];

  const rows: RawTableRow[] = [
    authorizationDisplay,
    startDate,
    endDate,
    workType,
    timingEvent,
    applicationMethod,
    reiExpiration,
    phiExpiration,
    equipment,
    recordCreated
  ];

  return formatRowsToTableItems(rows);
};
